import React, { useContext, useRef, useState, useEffect } from 'react';
import { 
  Box, 
  Flex, 
  Button, 
  Spacer, 
  Image, 
  IconButton, 
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Text,
  useDisclosure,
  HStack,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Tooltip
} from '@chakra-ui/react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiHome, FiPlus, FiMenu, FiLogOut, FiUser, FiCreditCard, FiAlertCircle, FiZap, FiBriefcase } from 'react-icons/fi';
import { Megaphone } from 'lucide-react';
import { GiTwoCoins } from 'react-icons/gi';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useSignupModal } from '../../contexts/SignupModalContext';
import api from '../../services/api';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const queryClient = useQueryClient();
  const { user, logout, token, fetchUserProfile } = useAuth();
  const { showSignupModal } = useSignupModal();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [impersonationInfo, setImpersonationInfo] = useState(null);
  
  // Check for impersonation state
  useEffect(() => {
    const impersonatingData = localStorage.getItem('impersonating');
    if (impersonatingData) {
      setImpersonationInfo(JSON.parse(impersonatingData));
    } else {
      setImpersonationInfo(null);
    }
  }, [user]);

  // Fetch subscription status when user is logged in
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (user && token) {
        try {
          localStorage.setItem('token', token);
          const { data } = await api.get('/api/payments/subscription-status');
          setSubscriptionStatus(data);
        } catch (error) {
          console.error('Error fetching subscription status:', error);
        }
      }
    };

    fetchSubscriptionStatus();
    
    // Listen for all credit-consuming service completion events and subscription changes to refresh credits
    const handleServiceCompleted = () => {
      if (user?.is_anonymous) {
        // For anonymous users, refresh user profile to get updated credits
        fetchUserProfile();
      } else {
        // For authenticated users, refresh subscription status
        fetchSubscriptionStatus();
      }
    };
    
    // Handle subscription changes (upgrades/downgrades)
    const handleSubscriptionChanged = () => {
      // Refresh user profile to get updated subscription tier
      fetchUserProfile();
      // Refresh subscription status to get updated credits and scheduled changes
      fetchSubscriptionStatus();
    };
    
    // Add event listeners for all credit-consuming services and subscription changes
    const events = [
      'exportCompleted',           // Download/export
      'enrichmentCompleted',       // Contact enrichment
      'verificationCompleted',     // Email verification
      'emailDraftCompleted',       // Email drafting
      'customFieldCompleted',      // Custom field generation
      'subscriptionUpgraded',      // Subscription upgraded
      'subscriptionDowngraded',    // Subscription downgraded
      'subscriptionChanged'        // Generic subscription change
    ];
    
    events.forEach(event => {
      if (event.includes('subscription')) {
        window.addEventListener(event, handleSubscriptionChanged);
      } else {
        window.addEventListener(event, handleServiceCompleted);
      }
    });
    
    return () => {
      events.forEach(event => {
        if (event.includes('subscription')) {
          window.removeEventListener(event, handleSubscriptionChanged);
        } else {
          window.removeEventListener(event, handleServiceCompleted);
        }
      });
    };
  }, [user, token, fetchUserProfile]);

  const handleHomeClick = () => {
    navigate('/');
    onClose();
  };
  
  const handleNewCampaignClick = () => {
    onClose();
    navigate('/campaign/new');
  };

  const handleLogout = () => {
    logout();
    onClose();
  };

  const handleProfileClick = () => {
    // Navigate to profile page
    navigate('/profile');
    onClose(); // Close the drawer if it's open
  };

  const handleBillingClick = () => {
    navigate('/billing');
    onClose();
  };

  const handleIntegrationsClick = () => {
    navigate('/integrations');
    onClose();
  };

  const handleOutboundSetupClick = () => {
    navigate('/outbound-setup');
    onClose();
  };

  const isOutboundSetupRoute =
    location.pathname === '/outbound-setup' || location.pathname === '/email/domains';

  const handleWorkspaceClick = () => {
    navigate('/workspace');
    onClose();
  };

  const handleExitImpersonation = () => {
    // Remove impersonation flag
    localStorage.removeItem('impersonating');
    setImpersonationInfo(null);
    
    // Reload to clear the user's session and return to admin view
    window.location.href = '/admin';
  };

  // Determine subscription tier badge based on subscription field
  const getSubscriptionBadge = () => {
    if (!user) return null;

    const tier = user.subscription || 'free';
    
    const tierMap = {
      'free': {
        text: 'FREE',
        colorScheme: 'gray',
        tooltip: 'Free plan - 100 credits/month'
      },
      'starter': {
        text: 'STARTER',
        colorScheme: 'blue',
        tooltip: 'Starter plan - 1,000 credits/month'
      },
      'basic': {
        text: 'BASIC',
        colorScheme: 'green',
        tooltip: 'Basic plan - 5,000 credits/month'
      },
      'pro': {
        text: 'PRO',
        colorScheme: 'purple',
        tooltip: 'Pro plan - 50,000 credits/month'
      }
    };

    return tierMap[tier] || tierMap['free'];
  };

  // Get credits display
  const getCreditsDisplay = () => {
    // For anonymous users, get credits from user object
    // For authenticated users, get from subscriptionStatus
    const credits = user?.is_anonymous 
      ? (user.credits || 0)
      : (subscriptionStatus?.credits || 0);
    
    if (credits === undefined && !user?.is_anonymous) return null;

    return {
      count: credits,
      text: credits.toLocaleString(),
      colorScheme: credits > 0 ? 'yellow' : 'gray',
      tooltip: `${credits.toLocaleString()} ${credits === 1 ? 'credit' : 'credits'} available`
    };
  };

  const subscriptionBadge = getSubscriptionBadge();
  const creditsDisplay = getCreditsDisplay();
  const isAnonymous = user?.is_anonymous === true;

  // Derive campaign name for the topbar breadcrumb. Two routes feed this:
  //   • /campaign/:id           → advisor-owned campaign (params.id)
  //   • /reviews/:campaignId    → shared campaign the user is reviewing
  // Each uses a different set of React Query caches, but the UX is
  // identical: cache-first, network fallback on hard refresh.
  const isReviewerRoute = location.pathname.startsWith('/reviews/');
  const campaignId = isReviewerRoute
    ? params?.campaignId || null
    : params?.id || null;
  const [fetchedCampaignName, setFetchedCampaignName] = useState(null);

  let cachedCampaignName = null;
  if (campaignId) {
    if (isReviewerRoute) {
      // Reviewer caches: per-campaign payload first, then the
      // "Shared with me" list, both keyed by the current user id.
      const userScopedKey = user?.id ? user.id : 'anonymous';
      const payload = queryClient.getQueryData([
        'reviewerCampaign',
        userScopedKey,
        campaignId,
      ]);
      if (payload?.campaign_name) {
        cachedCampaignName = payload.campaign_name;
      } else {
        const shares = queryClient.getQueryData([
          'sharedCampaigns',
          userScopedKey,
        ]);
        if (Array.isArray(shares)) {
          const found = shares.find((s) => s.campaign_id === campaignId);
          if (found?.campaign_name) cachedCampaignName = found.campaign_name;
        }
      }
    } else {
      const singleCampaign = queryClient.getQueryData(['campaign', campaignId]);
      if (singleCampaign?.name) {
        cachedCampaignName = singleCampaign.name;
      } else {
        const allCacheEntries = queryClient.getQueriesData({ queryKey: ['campaigns'] });
        for (const [, campaignsList] of allCacheEntries) {
          if (Array.isArray(campaignsList)) {
            const found = campaignsList.find(c => (c.id || c._id) === campaignId);
            if (found?.name) {
              cachedCampaignName = found.name;
              break;
            }
          }
        }
      }
    }
  }

  useEffect(() => {
    if (!campaignId || cachedCampaignName) {
      setFetchedCampaignName(null);
      return;
    }
    let cancelled = false;
    // Reviewer fallback hits a different list endpoint than the advisor
    // flow; the reviewer-scoped "GET /api/reviews/campaigns" is already
    // cached from HomePage in 99% of cases, but on a hard refresh of a
    // direct link we need to fill it here to avoid a blank breadcrumb.
    const fallbackUrl = isReviewerRoute ? '/api/reviews/campaigns' : '/api/campaigns';
    api.get(fallbackUrl)
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        if (isReviewerRoute) {
          const match = list.find(s => s.campaign_id === campaignId);
          if (match?.campaign_name) setFetchedCampaignName(match.campaign_name);
        } else {
          const match = list.find(c => (c.id || c._id) === campaignId);
          if (match?.name) setFetchedCampaignName(match.name);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [campaignId, cachedCampaignName, isReviewerRoute]);

  const campaignName = cachedCampaignName || fetchedCampaignName;
  
  return (
    <>
      {/* Impersonation Banner */}
      {impersonationInfo && (
        <Box 
          bg="orange.600" 
          color="white" 
          py={2} 
          px={4} 
          position="sticky" 
          top="0" 
          zIndex="1001"
          boxShadow="md"
        >
          <Flex align="center" justify="center" gap={3}>
            <FiAlertCircle size={20} />
            <Text fontWeight="bold">
              Admin Mode: Viewing as {impersonationInfo.name} ({impersonationInfo.email})
            </Text>
            <Button
              size="sm"
              colorScheme="blackAlpha"
              variant="solid"
              onClick={handleExitImpersonation}
              ml={4}
            >
              Exit Impersonation
            </Button>
          </Flex>
        </Box>
      )}
      
      <Box
        as="header"
        bg="#0a0a0a"
        color="#fafafa"
        py={0}
        px={2}
        borderBottomWidth="1px"
        borderBottomColor="rgba(255,255,255,0.08)"
        position="sticky"
        top={impersonationInfo ? "48px" : "0"}
        zIndex="1050"
        h="48px"
        minH="48px"
      >
        <Flex maxW="100%" mx="auto" align="center" justify="space-between" h="100%">
        {/* Logo + Campaign Name */}
        <Flex align="center" pl={1}>
          {isAnonymous ? (
            <Flex align="center">
              <Image src="/gillyreach-favicon.png" alt="GillyReach Logo" h="35px" />
            </Flex>
          ) : (
            <Flex align="center" as={Link} to="/" _hover={{ textDecoration: 'none' }}>
              <Image src="/gillyreach-favicon.png" alt="GillyReach Logo" h="35px" />
            </Flex>
          )}
          {campaignName ? (
            <>
              <Text mx={2} fontSize="sm" color="whiteAlpha.400" fontWeight="300" userSelect="none">/</Text>
              <Text
                fontSize="sm"
                fontWeight="500"
                color="whiteAlpha.800"
                noOfLines={1}
                maxW="280px"
                title={campaignName}
              >
                {campaignName}
              </Text>
            </>
          ) : location.pathname === '/integrations' ? (
            <>
              <Text mx={2} fontSize="sm" color="whiteAlpha.400" fontWeight="300" userSelect="none">/</Text>
              <Text fontSize="sm" fontWeight="500" color="whiteAlpha.800">Integrations</Text>
            </>
          ) : null}
        </Flex>
        
        {user ? (
          <HStack spacing={3} pr={1}>
            {/* Credits Display with Token Icon */}
            {creditsDisplay && (
              <Tooltip label={creditsDisplay.tooltip} placement="bottom">
                <HStack
                  spacing={1.5}
                  px={2.5}
                  py={1}
                  borderRadius="full"
                  cursor="pointer"
                  border="1px solid rgba(255,255,255,0.1)"
                  onClick={isAnonymous ? () => showSignupModal('general') : handleBillingClick}
                  _hover={{ bg: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.18)" }}
                  transition="all 0.15s ease"
                  display={{ base: 'none', sm: 'flex' }}
                >
                  <Box as={GiTwoCoins} color={creditsDisplay.count > 0 ? "#facc15" : "rgba(255,255,255,0.3)"} size="16px" />
                  <Text
                    fontWeight="500"
                    fontSize="sm"
                    color={creditsDisplay.count > 0 ? "#fafafa" : "rgba(255,255,255,0.35)"}
                  >
                    {creditsDisplay.text}
                  </Text>
                </HStack>
              </Tooltip>
            )}
            
            {/* Subscription Tier Badge */}
            {subscriptionBadge && !isAnonymous && (
              <Box position="relative" display={{ base: 'none', sm: 'block' }}>
                <Tooltip
                  label={
                    subscriptionStatus?.new_subscription && subscriptionStatus.new_subscription !== subscriptionStatus.subscription
                      ? subscriptionStatus.new_subscription === 'free'
                        ? `${subscriptionBadge.tooltip} (Cancelled - ends ${subscriptionStatus.next_period ? new Date(subscriptionStatus.next_period).toLocaleDateString() : 'soon'})`
                        : `${subscriptionBadge.tooltip} (Scheduled change to ${subscriptionStatus.new_subscription} on ${subscriptionStatus.next_period ? new Date(subscriptionStatus.next_period).toLocaleDateString() : 'next billing date'})`
                      : subscriptionBadge.tooltip
                  }
                  placement="bottom"
                >
                  <Box
                    px={2}
                    py="3px"
                    borderRadius="full"
                    bg="rgba(255,255,255,0.06)"
                    border="1px solid rgba(255,255,255,0.12)"
                    cursor="pointer"
                    onClick={handleBillingClick}
                    _hover={{ bg: "rgba(255,255,255,0.1)", borderColor: "rgba(255,255,255,0.22)" }}
                    transition="all 0.15s ease"
                  >
                    <Text fontSize="10px" fontWeight="600" color="rgba(255,255,255,0.55)" letterSpacing="0.08em" textTransform="uppercase">
                      {subscriptionBadge.text}
                    </Text>
                  </Box>
                </Tooltip>
                {/* Scheduled Change Indicator */}
                {subscriptionStatus?.new_subscription && subscriptionStatus.new_subscription !== subscriptionStatus.subscription && (
                  <Tooltip
                    label={subscriptionStatus.new_subscription === 'free' ? "Subscription cancelled" : "You have a scheduled plan change"}
                    placement="top"
                  >
                    <Box
                      position="absolute"
                      top="-3px"
                      right="-3px"
                      width="8px"
                      height="8px"
                      borderRadius="full"
                      bg={subscriptionStatus.new_subscription === 'free' ? 'rgba(239,68,68,0.9)' : 'rgba(251,146,60,0.9)'}
                      border="1.5px solid #0a0a0a"
                      cursor="pointer"
                      onClick={handleBillingClick}
                    />
                  </Tooltip>
                )}
              </Box>
            )}
            
            {/* Sign Up Free Button for Anonymous Users */}
            {isAnonymous && (
              <Button
                size="sm"
                bg="#fafafa"
                color="#0a0a0a"
                borderRadius="full"
                fontWeight="600"
                fontSize="sm"
                onClick={() => showSignupModal('general')}
                _hover={{ opacity: 0.85 }}
              >
                Sign Up Free
              </Button>
            )}
            
            {/* User Profile Menu */}
            <Menu closeOnSelect isLazy strategy="fixed">
              <MenuButton
                as={Button}
                variant="ghost"
                color="#fafafa"
                _hover={{ bg: "rgba(255,255,255,0.06)" }}
                _active={{ bg: "rgba(255,255,255,0.06)" }}
                _expanded={{ bg: "rgba(255,255,255,0.06)" }}
                _focus={{ boxShadow: "none", outline: "none" }}
                display="flex"
                alignItems="center"
                zIndex={1100}
              >
                <HStack>
                  <Avatar
                    size="sm"
                    name={`${user.first_name} ${user.last_name}`}
                    bg="rgba(255,255,255,0.1)"
                    color="#fafafa"
                  />
                  <Text display={{ base: 'none', md: 'block' }} fontSize="sm" color="#fafafa">
                    {user.first_name}
                  </Text>
                </HStack>
              </MenuButton>
              <MenuList
                bg="#0f0f0f"
                borderColor="rgba(255,255,255,0.10)"
                zIndex={1100}
                pointerEvents="auto"
                position="relative"
              >
                {isAnonymous ? (
                  <>
                    <MenuItem
                      icon={<FiUser color="#a3a3a3" />}
                      onClick={() => showSignupModal('general')}
                      _hover={{ bg: "rgba(255,255,255,0.06)" }}
                      _active={{ bg: "rgba(255,255,255,0.1)" }}
                      bg="#0f0f0f"
                      color="#fafafa"
                      fontSize="sm"
                    >
                      Sign Up Free
                    </MenuItem>
                  </>
                ) : (
                  <>
                    <MenuItem
                      icon={<FiUser color="#a3a3a3" />}
                      onClick={handleProfileClick}
                      _hover={{ bg: "rgba(255,255,255,0.06)" }}
                      _active={{ bg: "rgba(255,255,255,0.1)" }}
                      bg="#0f0f0f"
                      color="#fafafa"
                      fontSize="sm"
                    >
                      Profile
                    </MenuItem>
                    <MenuItem
                      icon={<FiZap color="#a3a3a3" />}
                      onClick={handleIntegrationsClick}
                      _hover={{ bg: "rgba(255,255,255,0.06)" }}
                      _active={{ bg: "rgba(255,255,255,0.1)" }}
                      bg="#0f0f0f"
                      color="#fafafa"
                      fontSize="sm"
                    >
                      Integrations
                    </MenuItem>
                    <MenuItem
                      icon={<Megaphone size={15} color="#a3a3a3" />}
                      onClick={handleOutboundSetupClick}
                      _hover={{ bg: "rgba(255,255,255,0.06)" }}
                      _active={{ bg: "rgba(255,255,255,0.1)" }}
                      bg="#0f0f0f"
                      color="#fafafa"
                      fontSize="sm"
                    >
                      Outbound setup
                    </MenuItem>
                    <MenuItem
                      icon={<FiCreditCard color="#a3a3a3" />}
                      onClick={handleBillingClick}
                      _hover={{ bg: "rgba(255,255,255,0.06)" }}
                      _active={{ bg: "rgba(255,255,255,0.1)" }}
                      bg="#0f0f0f"
                      color="#fafafa"
                      fontSize="sm"
                    >
                      Billing
                    </MenuItem>
                    <MenuItem
                      icon={<FiLogOut color="#a3a3a3" />}
                      onClick={handleLogout}
                      _hover={{ bg: "rgba(255,255,255,0.06)" }}
                      _active={{ bg: "rgba(255,255,255,0.1)" }}
                      bg="#0f0f0f"
                      color="#fafafa"
                      fontSize="sm"
                    >
                      Logout
                    </MenuItem>
                  </>
                )}
              </MenuList>
            </Menu>

            {/* Hamburger Menu Button */}
            {!isAnonymous && (
              <IconButton
                ref={btnRef}
                icon={<FiMenu />}
                variant="ghost"
                onClick={onOpen}
                aria-label="Menu"
                color="#fafafa"
                size="md"
                _hover={{ bg: "rgba(255,255,255,0.06)" }}
                _focus={{ boxShadow: "none", outline: "none" }}
              />
            )}
          </HStack>
        ) : (
          <HStack spacing={2}>
            <Button
              as={Link}
              to="/login"
              variant="ghost"
              color="#a3a3a3"
              fontSize="sm"
              _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
              _focus={{ boxShadow: "none" }}
            >
              Sign In
            </Button>
            <Button
              as={Link}
              to="/register"
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              fontSize="sm"
              h="36px"
              _hover={{ opacity: 0.85 }}
            >
              Register
            </Button>
          </HStack>
        )}
      </Flex>

      {/* Hamburger Menu Drawer - Now from right side */}
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent bg="#0a0a0a" color="#fafafa" borderLeft="1px solid rgba(255,255,255,0.08)">
          <DrawerCloseButton color="#a3a3a3" _hover={{ color: "#fafafa", bg: "rgba(255,255,255,0.06)" }} _focus={{ boxShadow: "none" }} />
          <DrawerHeader borderBottomWidth="1px" borderColor="rgba(255,255,255,0.08)" fontSize="sm" fontWeight="600" color="#a3a3a3" letterSpacing="0.06em" textTransform="uppercase">
            Menu
          </DrawerHeader>
          <DrawerBody px={3}>
            <VStack align="stretch" spacing={1} mt={3}>
              <Button
                leftIcon={<FiHome size={15} />}
                justifyContent="flex-start"
                variant="ghost"
                onClick={handleHomeClick}
                bg={location.pathname === '/' ? "rgba(255,255,255,0.08)" : "transparent"}
                color={location.pathname === '/' ? "#fafafa" : "#a3a3a3"}
                _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                _active={{ bg: "rgba(255,255,255,0.1)" }}
                width="100%"
                borderRadius="8px"
                px={4}
                py={5}
                fontSize="sm"
                fontWeight="500"
              >
                Home
              </Button>
              <Button
                leftIcon={<FiPlus size={15} />}
                justifyContent="flex-start"
                variant="ghost"
                onClick={handleNewCampaignClick}
                bg="transparent"
                color="#a3a3a3"
                _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                _active={{ bg: "rgba(255,255,255,0.1)" }}
                width="100%"
                borderRadius="8px"
                px={4}
                py={5}
                fontSize="sm"
                fontWeight="500"
              >
                New Campaign
              </Button>
              <Button
                leftIcon={<FiUser size={15} />}
                justifyContent="flex-start"
                variant="ghost"
                onClick={handleProfileClick}
                bg={location.pathname === '/profile' ? "rgba(255,255,255,0.08)" : "transparent"}
                color={location.pathname === '/profile' ? "#fafafa" : "#a3a3a3"}
                _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                _active={{ bg: "rgba(255,255,255,0.1)" }}
                width="100%"
                borderRadius="8px"
                px={4}
                py={5}
                fontSize="sm"
                fontWeight="500"
              >
                Profile
              </Button>
              <Button
                leftIcon={<FiZap size={15} />}
                justifyContent="flex-start"
                variant="ghost"
                onClick={handleIntegrationsClick}
                bg={location.pathname === '/integrations' ? "rgba(255,255,255,0.08)" : "transparent"}
                color={location.pathname === '/integrations' ? "#fafafa" : "#a3a3a3"}
                _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                _active={{ bg: "rgba(255,255,255,0.1)" }}
                width="100%"
                borderRadius="8px"
                px={4}
                py={5}
                fontSize="sm"
                fontWeight="500"
              >
                Integrations
              </Button>
              <Button
                leftIcon={<Megaphone size={15} />}
                justifyContent="flex-start"
                variant="ghost"
                onClick={handleOutboundSetupClick}
                bg={isOutboundSetupRoute ? "rgba(255,255,255,0.08)" : "transparent"}
                color={isOutboundSetupRoute ? "#fafafa" : "#a3a3a3"}
                _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                _active={{ bg: "rgba(255,255,255,0.1)" }}
                width="100%"
                borderRadius="8px"
                px={4}
                py={5}
                fontSize="sm"
                fontWeight="500"
              >
                Outbound setup
              </Button>
              <Button
                leftIcon={<FiCreditCard size={15} />}
                justifyContent="flex-start"
                variant="ghost"
                onClick={handleBillingClick}
                bg={location.pathname === '/billing' ? "rgba(255,255,255,0.08)" : "transparent"}
                color={location.pathname === '/billing' ? "#fafafa" : "#a3a3a3"}
                _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                _active={{ bg: "rgba(255,255,255,0.1)" }}
                width="100%"
                borderRadius="8px"
                px={4}
                py={5}
                fontSize="sm"
                fontWeight="500"
              >
                Billing
              </Button>
              <Button
                leftIcon={<FiLogOut size={15} />}
                justifyContent="flex-start"
                variant="ghost"
                onClick={handleLogout}
                color="#a3a3a3"
                _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                _active={{ bg: "rgba(255,255,255,0.1)" }}
                width="100%"
                borderRadius="8px"
                px={4}
                py={5}
                fontSize="sm"
                fontWeight="500"
              >
                Logout
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      </Box>
    </>
  );
};

export default Header; 